import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { AlertTriangle } from 'lucide-react';
import { captureException } from '@/lib/sentry';

interface Props {
  children: ReactNode;
  fallbackTitle?: string;
  fallbackMessage?: string;
}

interface State {
  hasError: boolean;
  error?: Error;
  componentStack?: string;
}

/**
 * React Error Boundary with Sentry integration
 * Catches component errors, reports to Sentry, and displays fallback UI
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log to console for development
    console.error('ErrorBoundary caught error:', error, errorInfo);

    this.setState({ componentStack: errorInfo.componentStack || undefined });

    // Report to Sentry with component stack
    captureException(error, {
      componentStack: errorInfo.componentStack,
      errorBoundary: this.constructor.name,
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined, componentStack: undefined });
  };

  handleHardReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const rawMessage = this.state.error?.message || 'Unknown error';
      const isReact31 = /Minified React error #31/.test(rawMessage);
      const friendlyMessage = isReact31
        ? "A component tried to render a raw object as text. Open technical details below and share them so this can be pinpointed."
        : rawMessage;

      return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-background">
          <Card className="max-w-xl w-full p-6 space-y-4">
            <div className="flex items-center gap-3 text-destructive">
              <AlertTriangle className="h-6 w-6" />
              <h2 className="text-xl font-semibold">
                {this.props.fallbackTitle || 'Something went wrong'}
              </h2>
            </div>

            <p className="text-muted-foreground">
              {this.props.fallbackMessage || friendlyMessage}
            </p>

            <details className="text-xs text-muted-foreground" open>
              <summary className="cursor-pointer hover:text-foreground">
                Technical details
              </summary>
              <pre className="mt-2 p-2 bg-muted rounded overflow-auto max-h-64 whitespace-pre-wrap break-words">
                {rawMessage}
                {this.state.componentStack ? `\n\nComponent stack:${this.state.componentStack}` : ''}
              </pre>
            </details>

            <div className="flex gap-2">
              <Button onClick={this.handleReset} className="flex-1">
                Try Again
              </Button>
              <Button variant="outline" onClick={this.handleHardReload} className="flex-1">
                Reload
              </Button>
              <Button
                variant="outline"
                onClick={() => window.location.href = '/'}
                className="flex-1"
              >
                Go Home
              </Button>
            </div>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

